import type { ReactNode } from 'react';
import { AgentCard } from '@/components/agent-chat/agent-card.component';
import {
  CardCost,
  CardFooter,
  CardMeta,
  CardShortOfCredits,
  CardText,
} from '@/components/agent-chat/card-parts.component';
import { useCardBalance } from '@/components/agent-chat/use-buy-credits.hook';
import { ListRowsLink } from '@/components/agent-chat/list-rows-link.component';
import type { ConfirmationRequest } from '@/hooks/use-agent-chat.hook';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import {
  listShapingCardView,
  listShapingIcon,
  listShapingTitle,
} from './list-shaping.util';

type ConfirmResult = { ok: boolean; aborted?: boolean } | void;

/** The countdown shows only in the card's last minute. */
const COUNTDOWN_FROM_SECONDS = 60;

/**
 * Clu's list-shaping confirmation (a column added, a column's values set or
 * filled, a skill run on a list) in the agent card shell: the action as the
 * title and the most it can cost at the right; the sentence with the rows it
 * reaches (a link that opens them on the list when a filter shows exactly
 * them) and the list; one muted line with the filter and what else it
 * writes; then Cancel and the action's verb, or Buy credits when the balance
 * can't cover the cost. The time left shows in the last minute only. The
 * main button locks both until the action answers and unlocks them when it
 * fails. Part of Agents: nothing shows while access loads or without it.
 */
export function ListShapingConfirmationCard({
  request,
  onConfirm,
  onReject,
  secondsLeft,
  countdown,
}: {
  request: ConfirmationRequest;
  onConfirm: () => void | Promise<ConfirmResult>;
  onReject: () => void;
  secondsLeft: number | null;
  countdown?: ReactNode;
}) {
  const { unlocked } = useAgentsAccess();
  const view = listShapingCardView(
    request as unknown as { tool_name?: string } & Record<string, unknown>
  );
  // Short by the balance sent with the card; a top-up since then lifts it.
  const have = useCardBalance(view?.short?.have);
  if (!unlocked) return null;

  const expired = secondsLeft !== null && secondsLeft <= 0;
  const showCountdown =
    !expired &&
    countdown &&
    secondsLeft !== null &&
    secondsLeft <= COUNTDOWN_FROM_SECONDS;
  const cost = view?.cost;
  const short =
    cost && view?.short && have !== null && cost.amount > have
      ? { have }
      : undefined;
  const trailing =
    !expired && (cost || showCountdown) ? (
      <span className='flex items-center gap-[8px]'>
        {cost ? (
          <CardCost amount={cost.amount} upTo={cost.upTo} short={!!short} />
        ) : null}
        {showCountdown ? countdown : null}
      </span>
    ) : undefined;

  const rows = view?.rows;
  // A link only when a list filter shows exactly these rows.
  const rowsPart = rows ? (
    rows.listId && rows.filters ? (
      <ListRowsLink listId={rows.listId} filters={rows.filters}>
        {rows.label}
      </ListRowsLink>
    ) : (
      <span data-card-rows>{rows.label}</span>
    )
  ) : null;

  return (
    <AgentCard
      icon={listShapingIcon(request.tool_name)}
      title={
        expired
          ? 'Action expired'
          : (view?.title ??
            listShapingTitle(request.tool_name, request.arguments?.mode))
      }
      data-list-shaping-confirmation
      trailing={trailing}
    >
      {expired ? (
        <CardText>This action expired. Please ask again.</CardText>
      ) : (
        <>
          <div className='flex flex-col gap-[4px]'>
            <CardText lines={0} className='[overflow-wrap:anywhere]'>
              {view ? (
                <>
                  {view.text.before}
                  {rowsPart ? <> {rowsPart}</> : null}
                  {view.text.after ? ` ${view.text.after}` : null}
                </>
              ) : (
                request.description
              )}
            </CardText>
            {view?.meta.length ? (
              <CardMeta className='[overflow-wrap:anywhere]'>
                {view.meta.join(' · ')}
              </CardMeta>
            ) : null}
            {short ? <CardShortOfCredits have={short.have} /> : null}
          </div>
          <CardFooter
            cancel={{ onClick: onReject }}
            primary={{
              label: view?.verb ?? 'Confirm',
              workingLabel: view?.working ?? 'Confirming',
              onClick: () => onConfirm(),
            }}
            short={short && cost ? { needed: cost.amount } : undefined}
          />
        </>
      )}
    </AgentCard>
  );
}
